import { CourseDetailsResponse, Video, Section } from '../../interfaces/api/courses';
/**
 * Checks if all items in a chapter are completed
 * Evaluates completion status for both video and quiz items
 *
 * @param {Section} section - The chapter to check for completion
 * @returns {boolean} True if all items in the chapter are completed, false otherwise
 *
 * @example
 * const isCompleted = isChapterCompleted(chapter);
 * if (isCompleted) {
 *   // Handle completed chapter
 * }
 */
export const isChapterCompleted = (section: Section | undefined): boolean => {
  if (!section) {
    return false;
  }
  return section.videos.every((item) => {
    return item.completed;
  });
};

/**
 * Processes course content to determine locked states for chapters and items
 * Applies the following rules:
 * - First chapter is always unlocked
 * - A chapter is locked if any item in the previous chapter is not completed
 * - In an unlocked chapter, items after the first uncompleted item are locked
 *
 * @param {CourseDetails} course - The course data to process
 * @returns {CourseDetails} Processed course with updated locked states
 *
 * @example
 * const processedCourse = processChapterProgress(courseData);
 * // Process course data with locked states
 * const chapters = processedCourse.chapters;
 */
export const processChapterProgress = (course: CourseDetailsResponse): CourseDetailsResponse => {
  if (!course?.sections) {
    return course;
  }

  const processedChapters = course.sections.map((chapter, chapterIndex) => {
    // Check if previous chapter exists and if its assessment is completed
    const isPreviousChapterCompleted =
      chapterIndex === 0
        ? true
        : course.sections[chapterIndex - 1]?.assessment.completed &&
          isChapterCompleted(course.sections[chapterIndex - 1]);

    // If previous chapter's assessment isn't completed, lock all videos in current chapter
    if (!isPreviousChapterCompleted) {
      return {
        ...chapter,
        videos: chapter.videos.map((item) => ({
          ...item,
          isLocked: true,
        })),
        assessment: {
          ...chapter.assessment,
          isLocked: true,
        },
      };
    }

    // Process videos sequentially within an unlocked chapter
    let foundFirstUncompleted = false;
    const processedVideos = chapter.videos.map((item) => {
      if (foundFirstUncompleted) {
        return { ...item, isLocked: true };
      }

      const isCompleted = item.completed;
      if (!isCompleted) {
        foundFirstUncompleted = true;
      }

      return { ...item, isLocked: false };
    });

    // Check if all videos in the chapter are completed to determine assessment lock status
    const areAllVideosCompleted = !chapter.videos.some((video) => !video.completed);

    return {
      ...chapter,
      videos: processedVideos,
      assessment: {
        ...chapter.assessment,
        isLocked: !areAllVideosCompleted,
      },
    };
  });
  const isFinalAssessmentLocked = processedChapters.some(
    (section) => section.videos.some((video) => !video.completed) || !section.assessment.completed,
  );
  return {
    ...course,
    sections: processedChapters,
    final_assessment: {
      ...course.final_assessment,
      isLocked: isFinalAssessmentLocked,
    },
  };
};

/**
 * Retrieves the first video item from a course
 * Safely handles potentially undefined values and returns undefined if no video is found
 *
 * @param {CourseDetails} course - The course to get the first video from
 * @returns {VideoItem | undefined} The first video item if found, undefined otherwise
 *
 * @example
 * const firstVideo = getFirstVideo(course);
 * if (firstVideo) {
 *   // Handle first video
 * }
 *
 * @throws {undefined} Returns undefined if course structure is invalid or first item is not a video
 */
export const getFirstVideo = (course: CourseDetailsResponse): Video | undefined => {
  try {
    const video = course.sections![0]?.videos![0];
    if (!video) {
      return undefined;
    }
    return video;
  } catch (e) {
    return undefined;
  }
};

export const formatDuration = (durationInSeconds: number): string => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = Math.floor(durationInSeconds % 60);

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};
